import { useCallback, useMemo, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolInvoke } from '@saas-pages/shared/public/runtime'
import { numberLocale, useLocale, useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'

const UTILITY = 'uuid-generator'
const UUID_EPOCH_HIGH = 0x01b21dd2
const UUID_EPOCH_LOW = 0x13814000

type UuidVersion = 'v1' | 'v4' | 'v6' | 'v7'

const UUID_VERSIONS: UuidVersion[] = ['v4', 'v7', 'v1', 'v6']

let lastTimestampMs = 0
let timestampSubMs = 0
let clockSequence: number | null = null
let nodeId: Uint8Array | null = null
let lastV7TimestampMs = 0
let v7Sequence = 0

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length)
  window.crypto.getRandomValues(bytes)
  return bytes
}

function getClockSequence(): number {
  if (clockSequence === null) {
    const bytes = randomBytes(2)
    clockSequence = (((bytes[0]! << 8) | bytes[1]!) & 0x3fff)
  }
  return clockSequence
}

function getNodeId(): Uint8Array {
  if (!nodeId) {
    nodeId = randomBytes(6)
    nodeId[0] = nodeId[0]! | 0x01
  }
  return nodeId
}

function toHex(value: number, length = 2): string {
  return value.toString(16).padStart(length, '0')
}

function formatBytes(bytes: Uint8Array): string {
  const hex = (index: number) => toHex(bytes[index]!)
  return (
    hex(0) + hex(1) + hex(2) + hex(3) + '-' +
    hex(4) + hex(5) + '-' +
    hex(6) + hex(7) + '-' +
    hex(8) + hex(9) + '-' +
    hex(10) + hex(11) + hex(12) + hex(13) + hex(14) + hex(15)
  )
}

function nextTimestamp100ns(): string {
  const now = Date.now()
  if (now === lastTimestampMs) {
    timestampSubMs = (timestampSubMs + 1) % 10_000
    if (timestampSubMs === 0) {
      clockSequence = (getClockSequence() + 1) & 0x3fff
    }
  } else {
    lastTimestampMs = now
    timestampSubMs = 0
  }

  const msHigh = Math.floor(now / 0x100000000)
  const msLow = now >>> 0
  const lowProduct = msLow * 10_000 + timestampSubMs
  let low = lowProduct >>> 0
  let high = msHigh * 10_000 + Math.floor(lowProduct / 0x100000000)

  low += UUID_EPOCH_LOW
  if (low > 0xffffffff) {
    low -= 0x100000000
    high += 1
  }
  high += UUID_EPOCH_HIGH

  return toHex(high & 0x0fffffff, 7) + toHex(low, 8)
}

function formatTimeUuid(version: 'v1' | 'v6'): string {
  const timestamp = nextTimestamp100ns()
  const sequence = getClockSequence()
  const node = getNodeId()
  const clockHi = toHex(((sequence >>> 8) & 0x3f) | 0x80)
  const clockLow = toHex(sequence & 0xff)
  const nodeHex = Array.from(node, (byte) => toHex(byte)).join('')

  if (version === 'v6') {
    return (
      timestamp.slice(0, 8) + '-' +
      timestamp.slice(8, 12) + '-' +
      '6' + timestamp.slice(12, 15) + '-' +
      clockHi + clockLow + '-' +
      nodeHex
    )
  }

  return (
    timestamp.slice(7, 15) + '-' +
    timestamp.slice(3, 7) + '-' +
    '1' + timestamp.slice(0, 3) + '-' +
    clockHi + clockLow + '-' +
    nodeHex
  )
}

function generateUuidV4(): string {
  const b = randomBytes(16)
  // Set version 4: bits 12-15 = 0100
  b[6] = (b[6]! & 0x0f) | 0x40
  // Set variant: bits 6-7 = 10
  b[8] = (b[8]! & 0x3f) | 0x80
  return formatBytes(b)
}

function generateUuidV7(): string {
  const now = Date.now()
  if (now === lastV7TimestampMs) {
    v7Sequence = (v7Sequence + 1) & 0x0fff
  } else {
    lastV7TimestampMs = now
    const random = randomBytes(2)
    v7Sequence = (((random[0]! << 8) | random[1]!) & 0x0fff)
  }

  const b = randomBytes(16)
  b[0] = Math.floor(now / 0x10000000000) & 0xff
  b[1] = Math.floor(now / 0x100000000) & 0xff
  b[2] = Math.floor(now / 0x1000000) & 0xff
  b[3] = Math.floor(now / 0x10000) & 0xff
  b[4] = Math.floor(now / 0x100) & 0xff
  b[5] = now & 0xff
  b[6] = 0x70 | ((v7Sequence >>> 8) & 0x0f)
  b[7] = v7Sequence & 0xff
  b[8] = (b[8]! & 0x3f) | 0x80
  return formatBytes(b)
}

function generateUuid(version: UuidVersion): string {
  switch (version) {
    case 'v1':
      return formatTimeUuid('v1')
    case 'v6':
      return formatTimeUuid('v6')
    case 'v7':
      return generateUuidV7()
    case 'v4':
    default:
      return generateUuidV4()
  }
}

export function UuidGeneratorPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [count, setCount] = useState(1)
  const [nonce, setNonce] = useState(0)
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null)
  const [copiedAll, setCopiedAll] = useState(false)
  const [upper, setUpper] = useState(false)
  const [hyphens, setHyphens] = useState(true)
  const [version, setVersion] = useState<UuidVersion>('v4')

  const uuids = useMemo(() => {
    const result: string[] = []
    for (let i = 0; i < count; i++) {
      result.push(generateUuid(version))
    }
    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, nonce, version])

  const displayUuids = useMemo(() => {
    return uuids.map((uuid) => {
      let value = uuid
      if (upper) value = value.toUpperCase()
      if (!hyphens) value = value.replace(/-/g, '')
      return value
    })
  }, [uuids, upper, hyphens])

  const handleGenerate = useCallback(() => {
    toolInvoke(UTILITY, { count, version, upper, hyphens })
    setNonce((n) => n + 1)
  }, [count, version, upper, hyphens])

  const handleCopyOne = useCallback(async (index: number) => {
    const value = displayUuids[index]
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
      setCopiedIndex(index)
      setCopiedAll(false)
      setTimeout(() => setCopiedIndex(null), 1500)
    } catch {
      // silently fail
    }
  }, [displayUuids])

  const handleCopyAll = useCallback(async () => {
    const text = displayUuids.join('\n')
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopiedAll(true)
      setCopiedIndex(null)
      setTimeout(() => setCopiedAll(false), 1500)
    } catch {
      // silently fail
    }
  }, [displayUuids])



  return (
    <ToolPageShell toolKey="uuidGenerator">
      {/* Controls */}
      <Card className="overflow-hidden shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b bg-muted/45 px-4 py-3">
          <div className="text-sm font-medium tracking-normal">
            {t.uuidGenerator.controls}
          </div>
        </div>
        <div className="grid gap-6 p-4 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              {t.uuidGenerator.version}
            </label>
            <div className="grid grid-cols-2 rounded-lg border bg-card p-0.5 shadow-sm">
              {UUID_VERSIONS.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setVersion(item)}
                  className={cn(
                    'rounded-md px-2 py-1.5 text-sm font-medium transition-colors',
                    version === item
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {item.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor="uuid-count"
              className="mb-1.5 block text-xs font-medium text-muted-foreground"
            >
              {t.uuidGenerator.count}
            </label>
            <input
              id="uuid-count"
              type="number"
              min={1}
              max={1000}
              value={count}
              onChange={(e) => {
                const v = Math.min(1000, Math.max(1, parseInt(e.target.value, 10) || 1))
                setCount(v)
              }}
              className={cn(
                'w-full rounded-lg border bg-card px-3 py-2 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-primary/35',
              )}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              {t.uuidGenerator.case}
            </label>
            <div className="flex rounded-lg border bg-card p-0.5 shadow-sm">
              <button
                type="button"
                onClick={() => setUpper(false)}
                className={cn(
                  'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  !upper
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                abc
              </button>
              <button
                type="button"
                onClick={() => setUpper(true)}
                className={cn(
                  'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  upper
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                ABC
              </button>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              {t.uuidGenerator.hyphens}
            </label>
            <div className="flex rounded-lg border bg-card p-0.5 shadow-sm">
              <button
                type="button"
                onClick={() => setHyphens(true)}
                className={cn(
                  'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  hyphens
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {t.uuidGenerator.withHyphens}
              </button>
              <button
                type="button"
                onClick={() => setHyphens(false)}
                className={cn(
                  'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  !hyphens
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {t.uuidGenerator.withoutHyphens}
              </button>
            </div>
          </div>

          <div className="flex items-end">
            <Button onClick={handleGenerate} className="w-full">
              {t.uuidGenerator.generate}
            </Button>
          </div>
        </div>
      </Card>

      {/* Results */}
      <Card className="mt-6 overflow-hidden shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-3">
          <div className="text-sm font-medium tracking-normal">
            {t.uuidGenerator.results} ({count})
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyAll}
              disabled={displayUuids.length === 0}
            >
              {copiedAll ? t.uuidGenerator.copied : t.uuidGenerator.copyAll}
            </Button>
          </div>
        </div>

        <div className="divide-y">
          {displayUuids.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {t.uuidGenerator.noResults}
            </div>
          )}

          {displayUuids.map((uuid, index) => (
            <div
              key={index}
              className={cn(
                'flex items-center justify-between gap-4 px-4 py-3 transition-colors',
                'hover:bg-muted/30',
              )}
            >
              <code className="select-all font-mono text-[13px] leading-6 tracking-wide">
                {uuid}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleCopyOne(index)}
                className="shrink-0"
              >
                {copiedIndex === index ? t.uuidGenerator.copied : t.uuidGenerator.copy}
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* Info cards */}
      <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="text-sm font-medium tracking-normal">{t.uuidGenerator.version}</div>
            <div className="mt-1 text-2xl font-bold tabular-nums">
              {version.toUpperCase()}
            </div>
            <div className="text-xs text-muted-foreground">
              {t.uuidGenerator.versionDescriptions[version]}
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="text-sm font-medium tracking-normal">{t.uuidGenerator.count}</div>
            <div className="mt-1 text-2xl font-bold tabular-nums">
              {count.toLocaleString(localeCode)}
            </div>
            <div className="text-xs text-muted-foreground">{version.toUpperCase()}</div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="text-sm font-medium tracking-normal">{t.uuidGenerator.case}</div>
            <div className="mt-1 text-2xl font-bold tabular-nums">
              {upper ? 'ABC' : 'abc'}
            </div>
            <div className="text-xs text-muted-foreground">
              {upper ? t.uuidGenerator.uppercase : t.uuidGenerator.lowercase}
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="text-sm font-medium tracking-normal">{t.uuidGenerator.hyphens}</div>
            <div className="mt-1 text-2xl font-bold tabular-nums">
              {hyphens ? '—' : '✕'}
            </div>
            <div className="text-xs text-muted-foreground">
              {hyphens ? t.uuidGenerator.withHyphens : t.uuidGenerator.withoutHyphens}
            </div>
          </CardContent>
        </Card>
      </section>
    </ToolPageShell>
  )
}
